/**
 * What linking a NOS Deal on the New Resource Plan form does to the fields it defaults.
 *
 * A Deal sets three fields — the CRM solution, the Contracted Fee (the Deal's amount)
 * and the fee's Source reference (the Deal's name). The CRM solution is not editable on
 * its own (it is always the Deal's); the fee and reference are. The Deal can be changed
 * before the plan is created, re-populating each time.
 *
 * The never-clobber rule, the same one the form applies to the auto-filled plan name and
 * Pod Shape:
 *   - a value the Deal HAS overwrites the field — that is the re-population;
 *   - a value the Deal LACKS (or "No deal") clears the field only when its current value
 *     is the one the previous Deal put there. Something the planner typed is kept.
 *
 * A Deal whose Solution is not one this planner can pick counts as having no Solution:
 * the field is left blank for them to fill in.
 */

/** The fee reference column is `max(120)` — a Deal name longer than that is cut. */
const REFERENCE_MAX = 120;

export interface DealDefaultFields {
  solutionId: string;
  contractedFee: string;
  contractedFeeReference: string;
}

export interface DealDefaultsSource {
  dealName: string;
  amount: string | null;
  solutionId: string | null;
}

/** What the last-linked Deal put in each field ("" for nothing). */
export const NO_DEAL_FILLED: DealDefaultFields = {
  solutionId: "",
  contractedFee: "",
  contractedFeeReference: "",
};

export function applyDealDefaults(args: {
  current: DealDefaultFields;
  /** What the previously linked Deal filled in. */
  filled: DealDefaultFields;
  /** The newly linked Deal, or null for "No deal". */
  deal: DealDefaultsSource | null;
  /** The Solutions this planner can pick. */
  visibleSolutionIds: ReadonlySet<string>;
}): { next: DealDefaultFields; filled: DealDefaultFields } {
  const { current, filled, deal, visibleSolutionIds } = args;

  const fromDeal: DealDefaultFields = {
    solutionId:
      deal?.solutionId != null && visibleSolutionIds.has(deal.solutionId) ? deal.solutionId : "",
    contractedFee: deal?.amount ?? "",
    contractedFeeReference: deal === null ? "" : deal.dealName.trim().slice(0, REFERENCE_MAX),
  };

  const pick = (key: keyof DealDefaultFields): string => {
    if (fromDeal[key] !== "") return fromDeal[key];
    return current[key] === filled[key] ? "" : current[key];
  };

  return {
    next: {
      solutionId: pick("solutionId"),
      contractedFee: pick("contractedFee"),
      contractedFeeReference: pick("contractedFeeReference"),
    },
    filled: fromDeal,
  };
}
